/**
 * Customer Cases Gallery — ForzaCCB
 *
 * Features:
 *  - Client-side filter by category pill + make dropdown + text search
 *  - Client-side pagination (no reload)
 *  - Mixed aspect-ratio galleries: detects portrait vs landscape from data-ar
 *  - Flickity carousel for multi-image cards
 *  - Video play/pause
 *  - Full-screen lightbox (like product page modal)
 *  - URL deep-linking for all filter state
 *  - Show More / Show Less for long descriptions on mobile
 */

(function () {
    'use strict';

    /* ------------------------------------------------------------------
       Selectors
       ------------------------------------------------------------------ */
    const S = {
        gallery: '[data-customer-cases-gallery]',
        grid: '[data-cc-grid]',
        card: '[data-cc-card]',
        carousel: '[data-cc-carousel]',
        categoryGroup: '[data-cc-category-group]',
        categoryBtn: '[data-cc-category-group] [data-cc-category]',
        makeSelect: '[data-cc-make-select]',
        searchInput: '[data-cc-search]',
        videoContainer: '[data-cc-video-container]',
        video: '[data-cc-video]',
        noResults: '[data-cc-no-results]',
        descWrapper: '[data-cc-description-wrapper]',
        descContent: '[data-cc-description]',
        showMoreBtn: '[data-cc-show-more-btn]',
        pagination: '[data-cc-pagination]',
        lightboxTrigger: '[data-cc-lightbox-trigger]',
        thumbSelect: '[data-cc-thumbs] [data-cc-thumb]',
        // Lightbox
        lightbox: '[data-cc-lightbox]',
        lbBackdrop: '[data-cc-lightbox-backdrop]',
        lbClose: '[data-cc-lightbox-close]',
        lbPrev: '[data-cc-lightbox-prev]',
        lbNext: '[data-cc-lightbox-next]',
        lbStage: '[data-cc-lightbox-stage]',
        lbImg: '[data-cc-lightbox-img]',
        lbDots: '[data-cc-lightbox-dots]',
        lbThumbs: '[data-cc-lightbox-thumbs]',
        lbInfo: '[data-cc-lightbox-info]',
    };

    /* ------------------------------------------------------------------
       Helpers
       ------------------------------------------------------------------ */
    function debounce(fn, ms) {
        let t;
        return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
    }

    /* ------------------------------------------------------------------
       Main class
       ------------------------------------------------------------------ */
    class CustomerCasesGallery {
        constructor(container) {
            this.container = container;
            this.grid = container.querySelector(S.grid);
            this.cards = [];
            this.categoryBtns = [];
            this.makeSelect = null;
            this.searchInput = null;
            this.noResults = null;
            this.paginationEl = null;

            // State
            this.currentCategory = 'all';
            this.currentMake = 'all';
            this.currentSearch = '';
            this.perPage = parseInt(container.querySelector(S.grid)?.dataset.perPage || '12', 10);
            this.currentPage = 1;
            this.filteredCards = [];

            // Lightbox state
            this.lightbox = document.querySelector(S.lightbox);
            this.lbImages = [];
            this.lbIndex = 0;
            this.lbPrevEl = null;
            this.lbNextEl = null;
            this.lbImgEl = null;
            this.lbDotsEl = null;
            this.lbInfoEl = null;

            this.init();
        }

        /* ----------------------------------------------------------------
           Init
           ---------------------------------------------------------------- */
        init() {
            this.cards = Array.from(this.container.querySelectorAll(S.card));
            this.dedupeCards();
            this.cards = Array.from(this.container.querySelectorAll(S.card));
            this.categoryBtns = Array.from(this.container.querySelectorAll(S.categoryBtn));
            this.makeSelect = this.container.querySelector(S.makeSelect);
            this.searchInput = this.container.querySelector(S.searchInput);
            this.noResults = this.container.querySelector(S.noResults);
            this.paginationEl = this.container.querySelector(S.pagination);

            // Per-page from section setting (passed via data attribute on grid)
            const pp = this.grid && parseInt(this.grid.dataset.perPage, 10);
            if (pp && !isNaN(pp)) this.perPage = pp;

            // Columns
            const cols = this.grid && this.grid.dataset.columns;
            if (cols === '3') this.grid.dataset.columns = '3'; // triggers CSS [data-columns="3"]

            this.detectCardAspectRatios();
            this.populateMakeSelect();
            this.addImageCountBadges();
            this.bindEvents();
            this.initCarousels();
            this.initVideos();
            this.initShowMore();
            this.initLightbox();
            this.parseUrlParams();
            this.applyFilters();
        }

        dedupeCards() {
            const seen = new Set();
            this.cards.forEach(card => {
                const title = [
                    card.querySelector('.customer-case-card__overlay-make')?.textContent || '',
                    card.querySelector('.customer-case-card__overlay-model')?.textContent || '',
                    card.querySelector('.customer-case-card__overlay-category')?.textContent || '',
                    card.querySelector('.customer-case-card__link-btn')?.getAttribute('href') || ''
                ].join('|');
                const key = (card.dataset.ccKey || title).replace(/\s+/g, ' ').toLowerCase().trim();

                if (!key) return;
                if (seen.has(key)) {
                    card.remove();
                    return;
                }
                seen.add(key);
            });
        }

        /* ----------------------------------------------------------------
           Aspect Ratio Detection
           Each card's gallery gets a dominant class based on its first
           image's data-ar attribute so the container sizes correctly.
           ---------------------------------------------------------------- */
        detectCardAspectRatios() {
            this.cards.forEach(card => {
                const gallery = card.querySelector('.customer-case-card__gallery');
                if (!gallery) return;

                const firstSlide = gallery.querySelector('.customer-case-card__gallery-slide');
                if (!firstSlide) return;

                const ar = parseFloat(firstSlide.dataset.ar || '1.78');

                if (ar < 0.85) {
                    // Portrait (9:16 or similar)
                    gallery.classList.add('is-portrait-dominant');
                } else if (ar > 1.4) {
                    // Landscape (16:9, 4:3 wide...)
                    gallery.classList.add('is-landscape-dominant');
                }
                // else: square-ish → default 4:3 from CSS
            });
        }

        /* ----------------------------------------------------------------
           Image Count Badges
           ---------------------------------------------------------------- */
        addImageCountBadges() {
            this.cards.forEach(card => {
                const gallery = card.querySelector('.customer-case-card__gallery');
                if (!gallery) return;
                const slides = gallery.querySelectorAll('.customer-case-card__gallery-slide');
                const count = slides.length;
                if (count > 1) {
                    gallery.dataset.imgCount = count + ' photos';
                }
            });
        }

        /* ----------------------------------------------------------------
           Make Dropdown Population
           ---------------------------------------------------------------- */
        populateMakeSelect() {
            if (!this.makeSelect) return;

            const makes = new Set();
            this.cards.forEach(card => {
                const make = card.dataset.ccMake;
                if (make && make.trim()) makes.add(make.trim());
            });

            // Remove old dynamic options
            Array.from(this.makeSelect.options).slice(1).forEach(o => o.remove());

            const sortedMakes = Array.from(makes).sort();
            sortedMakes.forEach(make => {
                const option = document.createElement('option');
                option.value = make;
                option.textContent = make;
                this.makeSelect.appendChild(option);
            });
        }

        /* ----------------------------------------------------------------
           Events
           ---------------------------------------------------------------- */
        bindEvents() {
            // Category pills
            const categoryGroup = this.container.querySelector(S.categoryGroup);
            if (categoryGroup) {
                categoryGroup.addEventListener('click', e => {
                    const btn = e.target.closest('[data-cc-category]');
                    if (btn) {
                        this.categoryBtns.forEach(b => b.classList.remove('is-active'));
                        btn.classList.add('is-active');
                        this.currentCategory = btn.dataset.ccCategory;
                        this.currentPage = 1;
                        this.applyFilters();
                        this.updateUrlParams();
                    }
                });
            }

            // Make dropdown
            if (this.makeSelect) {
                this.makeSelect.addEventListener('change', e => {
                    this.currentMake = e.target.value;
                    this.currentPage = 1;
                    this.applyFilters();
                    this.updateUrlParams();
                });
            }

            // Search
            if (this.searchInput) {
                this.searchInput.addEventListener('input', debounce(e => {
                    this.currentSearch = e.target.value.toLowerCase().trim();
                    this.currentPage = 1;
                    this.applyFilters();
                    this.updateUrlParams();
                }, 220));
            }

            // Card thumbnail row — switch main image without opening lightbox
            this.container.addEventListener('click', e => {
                const thumb = e.target.closest(S.thumbSelect);
                if (!thumb) return;
                e.preventDefault();
                e.stopPropagation();

                const card = thumb.closest(S.card);
                const gallery = card && card.querySelector('.customer-case-card__gallery');
                const idx = parseInt(thumb.dataset.ccThumbIndex, 10) || 0;

                if (gallery && gallery.flickityInstance) {
                    gallery.flickityInstance.select(idx);
                } else if (gallery) {
                    const slides = gallery.querySelectorAll('.customer-case-card__gallery-slide');
                    slides.forEach((s, i) => s.style.display = i === idx ? '' : 'none');
                }

                thumb.parentElement.querySelectorAll('[data-cc-thumb]').forEach(t => t.classList.remove('is-active'));
                thumb.classList.add('is-active');
            });

            // Theme Editor
            if (window.Shopify && window.Shopify.designMode) {
                document.addEventListener('shopify:section:load', e => {
                    if (e.target.querySelector(S.gallery)) this.reinit();
                });
                document.addEventListener('shopify:block:select', e => {
                    const card = e.target.closest(S.card);
                    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
            }
        }

        /* ----------------------------------------------------------------
           Filtering + Pagination
           ---------------------------------------------------------------- */
        applyFilters() {
            this.filteredCards = this.cards.filter(card => {
                const cat = card.dataset.ccCategory || '';
                const make = card.dataset.ccMake || '';
                const search = card.dataset.ccSearch || '';

                const matchCat = this.currentCategory === 'all' || cat === this.currentCategory;
                const matchMake = this.currentMake === 'all' || make === this.currentMake;
                const matchSearch = !this.currentSearch || search.includes(this.currentSearch);

                return matchCat && matchMake && matchSearch;
            });

            // Show/hide all cards first
            this.cards.forEach(card => card.classList.add('is-hidden'));

            // Paginate
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            const pageCards = this.filteredCards.slice(start, end);

            pageCards.forEach(card => {
                card.classList.remove('is-hidden');

                // Resize flickity for newly revealed cards
                const carousel = card.querySelector(S.carousel);
                if (carousel && carousel.flickityInstance) {
                    setTimeout(() => {
                        carousel.flickityInstance.resize();
                        carousel.flickityInstance.reposition();
                    }, 50);
                }
            });

            // No results
            if (this.noResults) {
                if (this.filteredCards.length === 0 && this.cards.length > 0) {
                    this.noResults.classList.add('is-visible');
                } else {
                    this.noResults.classList.remove('is-visible');
                }
            }

            // Render pagination
            this.renderPagination();
        }

        /* ----------------------------------------------------------------
           Pagination Rendering
           ---------------------------------------------------------------- */
        renderPagination() {
            if (!this.paginationEl) return;

            const total = Math.ceil(this.filteredCards.length / this.perPage);

            if (total <= 1) {
                this.paginationEl.innerHTML = '';
                return;
            }

            let html = '';

            // Prev
            html += `<button class="cc-page-btn" data-page="prev" ${this.currentPage === 1 ? 'disabled' : ''} aria-label="Previous page">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
      </button>`;

            // Page numbers: show max 7, with ellipsis
            const pages = this.buildPageRange(this.currentPage, total);
            pages.forEach(p => {
                if (p === '...') {
                    html += `<span style="padding:0 0.4rem;color:var(--cc-muted);">...</span>`;
                } else {
                    html += `<button class="cc-page-btn ${p === this.currentPage ? 'is-active' : ''}" data-page="${p}" aria-label="Page ${p}" ${p === this.currentPage ? 'aria-current="page"' : ''}>${p}</button>`;
                }
            });

            // Next
            html += `<button class="cc-page-btn" data-page="next" ${this.currentPage === total ? 'disabled' : ''} aria-label="Next page">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
      </button>`;

            this.paginationEl.innerHTML = html;

            this.paginationEl.querySelectorAll('[data-page]').forEach(btn => {
                btn.addEventListener('click', () => {
                    const p = btn.dataset.page;
                    const total2 = Math.ceil(this.filteredCards.length / this.perPage);

                    if (p === 'prev') this.currentPage = Math.max(1, this.currentPage - 1);
                    else if (p === 'next') this.currentPage = Math.min(total2, this.currentPage + 1);
                    else this.currentPage = parseInt(p, 10);

                    this.applyFilters();
                    this.updateUrlParams();

                    // Scroll to grid top
                    const gridTop = this.grid.getBoundingClientRect().top + window.scrollY - 100;
                    window.scrollTo({ top: gridTop, behavior: 'smooth' });
                });
            });
        }

        buildPageRange(current, total) {
            if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

            const pages = [];
            pages.push(1);

            if (current > 3) pages.push('...');

            const start = Math.max(2, current - 1);
            const end = Math.min(total - 1, current + 1);

            for (let i = start; i <= end; i++) pages.push(i);

            if (current < total - 2) pages.push('...');
            pages.push(total);

            return pages;
        }

        /* ----------------------------------------------------------------
           URL Deep Linking
           ---------------------------------------------------------------- */
        parseUrlParams() {
            const params = new URLSearchParams(window.location.search);

            const makeParam = params.get('make');
            if (makeParam && this.makeSelect) {
                const opt = Array.from(this.makeSelect.options).find(
                    o => o.value.toLowerCase() === makeParam.toLowerCase()
                );
                if (opt) {
                    this.makeSelect.value = opt.value;
                    this.currentMake = opt.value;
                }
            }

            const catParam = params.get('category');
            if (catParam) {
                const btn = this.categoryBtns.find(b => b.dataset.ccCategory === catParam);
                if (btn) {
                    this.categoryBtns.forEach(b => b.classList.remove('is-active'));
                    btn.classList.add('is-active');
                    this.currentCategory = catParam;
                }
            }

            const searchParam = params.get('search');
            if (searchParam && this.searchInput) {
                this.searchInput.value = searchParam;
                this.currentSearch = searchParam.toLowerCase().trim();
            }

            const pageParam = parseInt(params.get('page') || '1', 10);
            if (pageParam > 1) this.currentPage = pageParam;
        }

        updateUrlParams() {
            const url = new URL(window.location.href);

            if (this.currentCategory !== 'all') url.searchParams.set('category', this.currentCategory);
            else url.searchParams.delete('category');

            if (this.currentMake !== 'all') url.searchParams.set('make', this.currentMake);
            else url.searchParams.delete('make');

            if (this.currentSearch) url.searchParams.set('search', this.currentSearch);
            else url.searchParams.delete('search');

            if (this.currentPage > 1) url.searchParams.set('page', this.currentPage);
            else url.searchParams.delete('page');

            window.history.replaceState({}, '', url);
        }

        /* ----------------------------------------------------------------
           Flickity Carousels
           ---------------------------------------------------------------- */
        initCarousels() {
            if (typeof Flickity === 'undefined') {
                setTimeout(() => this.initCarousels(), 120);
                return;
            }

            this.container.querySelectorAll(S.carousel).forEach(carousel => {
                if (carousel.flickityInstance) return;

                const flkty = new Flickity(carousel, {
                    cellAlign: 'left',
                    contain: true,
                    wrapAround: true,
                    pageDots: true,
                    prevNextButtons: true,
                    draggable: true,
                    adaptiveHeight: false,
                    lazyLoad: 1,
                    imagesLoaded: true,
                });

                carousel.flickityInstance = flkty;
            });
        }

        /* ----------------------------------------------------------------
           Video Play/Pause
           ---------------------------------------------------------------- */
        initVideos() {
            this.container.querySelectorAll(S.videoContainer).forEach(vc => {
                const video = vc.querySelector(S.video);
                if (!video) return;

                vc.addEventListener('click', () => {
                    if (video.paused) { video.play(); vc.classList.add('is-playing'); }
                    else { video.pause(); vc.classList.remove('is-playing'); }
                });

                video.addEventListener('play', () => vc.classList.add('is-playing'));
                video.addEventListener('pause', () => vc.classList.remove('is-playing'));
            });
        }

        /* ----------------------------------------------------------------
           Show More (mobile descriptions)
           ---------------------------------------------------------------- */
        initShowMore() {
            const isMobile = window.innerWidth < 750;

            this.cards.forEach(card => {
                const wrapper = card.querySelector(S.descWrapper);
                const content = card.querySelector(S.descContent);
                const btn = card.querySelector(S.showMoreBtn);
                if (!wrapper || !content || !btn) return;

                wrapper.classList.remove('is-expanded');
                btn.classList.remove('is-visible', 'is-expanded');
                btn.childNodes[0].textContent = 'Show More';

                if (isMobile && content.offsetHeight > 120) {
                    btn.classList.add('is-visible');
                    btn.onclick = () => {
                        const expanded = wrapper.classList.toggle('is-expanded');
                        btn.classList.toggle('is-expanded', expanded);
                        btn.childNodes[0].textContent = expanded ? 'Show Less' : 'Show More';
                        if (!expanded) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    };
                }
            });
        }

        /* ----------------------------------------------------------------
           Lightbox
           ---------------------------------------------------------------- */
        initLightbox() {
            if (!this.lightbox) return;

            this.lbImgEl = this.lightbox.querySelector(S.lbImg);
            this.lbThumbsEl = this.lightbox.querySelector(S.lbThumbs);
            this.lbInfoEl = this.lightbox.querySelector(S.lbInfo);
            this.lbPrevEl = this.lightbox.querySelector(S.lbPrev);
            this.lbNextEl = this.lightbox.querySelector(S.lbNext);

            // Open: click on gallery
            this.container.addEventListener('click', e => {
                const trigger = e.target.closest(S.lightboxTrigger);
                if (!trigger) return;

                // Don't open if clicking on Flickity buttons/dots
                if (e.target.closest('.flickity-button, .flickity-page-dots')) return;

                // Don't open for video
                if (e.target.closest(S.videoContainer)) return;

                const card = trigger.closest(S.card);
                const gallery = trigger.classList.contains('customer-case-card__gallery')
                    ? trigger
                    : card && card.querySelector('.customer-case-card__gallery');
                if (!gallery) return;

                const slides = Array.from(gallery.querySelectorAll('.customer-case-card__gallery-slide'));
                if (slides.length === 0) return;

                // Determine starting index: explicit thumb index, else Flickity selection
                let startIdx = 0;
                if (trigger.dataset.ccThumbIndex !== undefined) {
                    startIdx = parseInt(trigger.dataset.ccThumbIndex, 10) || 0;
                } else if (gallery.flickityInstance) {
                    startIdx = gallery.flickityInstance.selectedIndex;
                }

                const imgs = slides.map(slide => {
                    const img = slide.querySelector('img');
                    return {
                        src: img ? (img.dataset.src || img.src || img.currentSrc || '') : '',
                        alt: img ? (img.alt || '') : '',
                    };
                }).filter(i => i.src);

                if (imgs.length === 0) return;

                // Build metadata for lightbox info panel
                const make = card ? (card.querySelector('.customer-case-card__overlay-make')?.textContent?.trim() || '') : '';
                const model = card ? (card.querySelector('.customer-case-card__overlay-model')?.textContent?.trim() || '') : '';
                const category = card ? (card.querySelector('.customer-case-card__overlay-category')?.textContent?.trim() || '') : '';
                const descHtml = card ? (card.querySelector('.customer-case-card__desc')?.innerHTML?.trim() || '') : '';
                const title = [make, model].filter(Boolean).join(' ');

                this.openLightbox(imgs, startIdx, {
                    title: title,
                    category: category,
                    descriptionHtml: descHtml
                });
            });

            // Close
            const close = this.lightbox.querySelector(S.lbClose);
            const backdrop = this.lightbox.querySelector(S.lbBackdrop);

            if (close) close.addEventListener('click', () => this.closeLightbox());
            if (backdrop) backdrop.addEventListener('click', () => this.closeLightbox());

            // Prev / Next
            if (this.lbPrevEl) this.lbPrevEl.addEventListener('click', () => this.lbGo(-1));
            if (this.lbNextEl) this.lbNextEl.addEventListener('click', () => this.lbGo(1));

            // Keyboard
            document.addEventListener('keydown', e => {
                if (this.lightbox.hasAttribute('hidden')) return;
                if (e.key === 'Escape') this.closeLightbox();
                if (e.key === 'ArrowLeft') this.lbGo(-1);
                if (e.key === 'ArrowRight') this.lbGo(1);
            });

            // Touch swipe
            let touchStartX = null;
            this.lightbox.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
            this.lightbox.addEventListener('touchend', e => {
                if (touchStartX === null) return;
                const dx = e.changedTouches[0].clientX - touchStartX;
                if (Math.abs(dx) > 40) this.lbGo(dx < 0 ? 1 : -1);
                touchStartX = null;
            });
        }

        openLightbox(images, startIndex, meta) {
            this.lbImages = images;
            this.lbIndex = startIndex;
            this.lbMeta = meta || {};

            this.lightbox.removeAttribute('hidden');
            document.body.style.overflow = 'hidden';

            this.lbRender();
        }

        closeLightbox() {
            this.lightbox.setAttribute('hidden', '');
            document.body.style.overflow = '';
        }

        lbGo(delta) {
            const len = this.lbImages.length;
            this.lbIndex = ((this.lbIndex + delta) + len) % len;
            this.lbRender();
        }

        lbRender() {
            if (!this.lbImgEl) return;

            const img = this.lbImages[this.lbIndex];
            const total = this.lbImages.length;

            // Use high-res source: replace width param
            const fullSrc = img.src.replace(/&width=\d+/, '&width=1600').replace(/\?width=\d+/, '?width=1600');

            this.lbImgEl.classList.add('is-loading');
            this.lbImgEl.src = fullSrc;
            this.lbImgEl.alt = img.alt;
            this.lbImgEl.onload = () => this.lbImgEl.classList.remove('is-loading');

            // Thumbnails
            if (this.lbThumbsEl) {
                if (total <= 1) {
                    this.lbThumbsEl.innerHTML = '';
                    if (this.lbThumbsEl.parentElement) this.lbThumbsEl.parentElement.hidden = true;
                } else {
                    if (this.lbThumbsEl.parentElement) this.lbThumbsEl.parentElement.hidden = false;

                    // Only rebuild if images changed or empty
                    const currentThumbsCount = this.lbThumbsEl.querySelectorAll('.cc-one-thumb').length;
                    if (currentThumbsCount !== total) {
                        this.lbThumbsEl.innerHTML = this.lbImages.map((img, i) => {
                            // Use smaller size for thumbs
                            const thumbSrc = img.src.replace(/&width=\d+/, '&width=150').replace(/\?width=\d+/, '?width=150');
                            return `<div class="cc-one-thumb" data-idx="${i}"><img src="${thumbSrc}" alt=""></div>`;
                        }).join('');

                        this.lbThumbsEl.querySelectorAll('.cc-one-thumb').forEach(thumb => {
                            thumb.addEventListener('click', () => {
                                this.lbIndex = parseInt(thumb.dataset.idx, 10);
                                this.lbRender();
                            });
                        });
                    }

                    // Highlight active
                    this.lbThumbsEl.querySelectorAll('.cc-one-thumb').forEach((thumb, i) => {
                        thumb.classList.toggle('is-active', i === this.lbIndex);
                        if (i === this.lbIndex) {
                            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                        }
                    });
                }
            }

            // Info
            if (this.lbInfoEl) {
                const countStr = total > 1 ? `${this.lbIndex + 1} / ${total}` : '';
                const title = this.lbMeta && this.lbMeta.title ? this.lbMeta.title : '';
                const category = this.lbMeta && this.lbMeta.category ? this.lbMeta.category : '';
                const descriptionHtml = this.lbMeta && this.lbMeta.descriptionHtml ? this.lbMeta.descriptionHtml : '';

                let infoHtml = '';
                infoHtml += '<div class="cc-lightbox__info-head">';
                infoHtml += `<p class="cc-lightbox__info-title">${title || 'Customer Case'}</p>`;
                if (countStr) {
                    infoHtml += `<span class="cc-lightbox__info-count">${countStr}</span>`;
                }
                infoHtml += '</div>';

                if (category) {
                    infoHtml += `<p class="cc-lightbox__info-category">${category}</p>`;
                }

                if (descriptionHtml) {
                    infoHtml += `<div class="cc-lightbox__desc rte">${descriptionHtml}</div>`;
                }

                this.lbInfoEl.innerHTML = infoHtml;
            }

            // Hide nav if single
            if (this.lbPrevEl) this.lbPrevEl.style.display = total <= 1 ? 'none' : '';
            if (this.lbNextEl) this.lbNextEl.style.display = total <= 1 ? 'none' : '';
        }

        /* ----------------------------------------------------------------
           Reinitialize (Theme Editor)
           ---------------------------------------------------------------- */
        reinit() {
            this.cards = Array.from(this.container.querySelectorAll(S.card));
            this.categoryBtns = Array.from(this.container.querySelectorAll(S.categoryBtn));
            this.makeSelect = this.container.querySelector(S.makeSelect);
            this.searchInput = this.container.querySelector(S.searchInput);
            this.noResults = this.container.querySelector(S.noResults);
            this.paginationEl = this.container.querySelector(S.pagination);

            if (this.makeSelect) {
                const first = this.makeSelect.options[0];
                this.makeSelect.innerHTML = '';
                this.makeSelect.appendChild(first);
                this.populateMakeSelect();
            }

            this.detectCardAspectRatios();
            this.addImageCountBadges();
            this.initCarousels();
            this.initVideos();
            this.initShowMore();
            this.currentPage = 1;
            this.applyFilters();
        }
    }

    /* ------------------------------------------------------------------
       Bootstrap
       ------------------------------------------------------------------ */
    function init() {
        document.querySelectorAll(S.gallery).forEach(gallery => {
            if (!gallery._ccInstance) {
                gallery._ccInstance = new CustomerCasesGallery(gallery);
            }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    document.addEventListener('shopify:section:load', e => {
        const g = e.target.querySelector(S.gallery);
        if (g && !g._ccInstance) g._ccInstance = new CustomerCasesGallery(g);
    });

})();
